import Icon from "../../components/Icon"
import { FieldIcon, iconInputClass, initials } from "./BillFormParts"

// Customer pieces of the "Issue a bill" form. The AKA ("also known as") is a
// private name the biller gives a customer; only that biller sees it.

export const MAX_AKA_LENGTH = 60

export const formatAka = (aka) => `AKA: ${String(aka || "").trim() || "---"}`

export function AkaField({ value, onChange }) {
  return (
    <div>
      <label htmlFor="ut-bill-customer-aka" className="mb-1 block text-xs font-semibold text-slate-500">
        AKA <span className="font-normal text-slate-400">(optional)</span>
      </label>
      <div className="relative">
        <FieldIcon icon="tag" />
        <input
          id="ut-bill-customer-aka"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={iconInputClass}
          placeholder="e.g. Mama Njeri"
          maxLength={MAX_AKA_LENGTH}
          autoComplete="off"
        />
      </div>
      <p className="mt-1 text-[11px] text-slate-400">Only you see this name. You can search by it next time.</p>
    </div>
  )
}

export function LinkedCustomerCard({ name, aka, changeLabel, onChange }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
      <span className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white">
          {initials(name)}
        </span>
        <span className="min-w-0 truncate">
          <span className="block truncate text-sm font-bold text-emerald-900">{name}</span>
          <span className="block truncate text-xs text-emerald-600">{formatAka(aka)}</span>
        </span>
      </span>
      <button type="button" onClick={onChange} className="shrink-0 text-xs font-bold text-emerald-700 underline">
        {changeLabel}
      </button>
    </div>
  )
}

export function NewCustomerFields({ form, onField, onBack }) {
  const field = (key) => ({
    value: form[key],
    onChange: (event) => onField(key, event.target.value),
    className: iconInputClass,
  })

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
          <Icon name="person-plus-fill" size="0.75rem" className="text-teal-600" />
          New customer
        </p>
        <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs font-bold text-teal-700">
          <Icon name="arrow-left" size="0.7rem" />
          Back to search
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <div className="relative">
          <FieldIcon icon="person" />
          <input {...field("name")} placeholder="Full name" required />
        </div>
        <div className="relative">
          <FieldIcon icon="telephone" />
          <input {...field("phone")} placeholder="Phone" required />
        </div>
        <div className="relative">
          <FieldIcon icon="person-vcard" />
          <input {...field("idNumber")} placeholder="ID number" required />
        </div>
        <div className="relative">
          <FieldIcon icon="shield-lock" />
          <input {...field("pin")} placeholder="4-digit PIN" inputMode="numeric" maxLength={4} required />
        </div>
      </div>
      <AkaField value={form.aka} onChange={(value) => onField("aka", value)} />
    </div>
  )
}

// Results show only the name and the biller's own AKA, never a phone or ID number.
export function CustomerSearchResults({ results, onSelect }) {
  if (!results.length) return null
  return (
    <div className="mt-1.5 max-h-44 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      {results.map((customer) => (
        <button
          key={customer._id}
          type="button"
          onClick={() => onSelect(customer)}
          className="flex w-full items-center gap-2.5 border-b border-slate-100 px-3 py-2 text-left last:border-0 hover:bg-teal-50"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-600">
            {initials(customer.name)}
          </span>
          <span className="min-w-0 truncate">
            <span className="block truncate text-sm font-semibold text-slate-800">{customer.name}</span>
            <span className="block truncate text-xs text-slate-500">{formatAka(customer.alsoKnownAs)}</span>
          </span>
        </button>
      ))}
    </div>
  )
}
